import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getContacts } from "../api/client.js";
import CrmContactDrawer from "./CrmContactDrawer.jsx";
import { formatContactType } from "./formatters.js";

const PAGE_SIZE_OPTIONS = [12, 24, 36, 48];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

function renderPrimaryField(contact) {
  if (contact?.emails?.length) return contact.emails[0];
  if (contact?.phones?.length) return contact.phones[0];
  return "No email or phone";
}

export function CrmContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");

  const fetchContacts = useCallback(() => {
    setLoading(true);
    setError("");
    getContacts({ page, pageSize })
      .then((payload) => {
        setContacts(payload.items || []);
        setTotal(payload.total || 0);
      })
      .catch((err) => {
        setError(err.message || "Unable to load contacts");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, pageSize]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts, refreshKey]);

  useEffect(() => {
    if (!statusMessage) return undefined;
    const timer = setTimeout(() => setStatusMessage(""), 3300);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const showingStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingEnd = total === 0 ? 0 : Math.min(total, showingStart + contacts.length - 1);

  const handlePageChange = (nextPage) => {
    if (nextPage === page) return;
    setSelectedContactId(null);
    setPage(Math.min(totalPages, Math.max(1, nextPage)));
  };

  const handlePageSizeChange = (event) => {
    const value = Number(event.target.value);
    if (!value) return;
    setSelectedContactId(null);
    setPageSize(value);
    setPage(1);
  };

  const handleContactSaved = (updated) => {
    setContacts((prev) =>
      prev.map((contact) => (contact.id === updated.id ? { ...contact, ...updated } : contact))
    );
    setStatusMessage("Contact saved");
  };

  const handleContactDeleted = (id) => {
    setSelectedContactId(null);
    setStatusMessage("Contact deleted");
    setRefreshKey((prev) => prev + 1);
  };

  const handleRetry = () => setRefreshKey((prev) => prev + 1);

  const createLocalContact = () => {
    const mock = {
      id: `local-${Date.now()}`,
      name: "Mock contact",
      type: "BUYER",
      emails: ["demo@home.com"],
      phones: ["+441234567890"],
      tags: ["demo"],
      contactListings: [],
      listingsOwned: [],
      updatedAt: new Date().toISOString()
    };
    setContacts((prev) => [mock, ...prev]);
    setStatusMessage("Mock contact added (local only)");
    setSelectedContactId(mock.id);
  };

  const hasContacts = contacts.length > 0;
  const isFirstPage = page <= 1;
  const isLastPage = page >= totalPages;

  return (
    <>
      <div className="space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">CRM contacts</p>
            <h1 className="text-3xl font-semibold text-white">Contacts</h1>
            <p className="text-sm text-slate-400">
              Showing {showingStart}-{showingEnd} of {total ?? "0"} clients
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="min-w-[220px] text-right text-xs uppercase tracking-[0.3em] text-emerald-200">
              {statusMessage || " "}
            </div>
            <button
              type="button"
              onClick={() => navigate("/crm/contacts/new")}
              className="rounded-full border border-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:border-emerald-400/40 hover:bg-emerald-500/10"
            >
              Add contact
            </button>
          </div>
        </header>
        {error && (
          <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
            <p>Unable to load contacts. {error}</p>
            <div className="mt-2 flex gap-3">
              <button
                type="button"
                onClick={handleRetry}
                className="text-sm font-semibold text-rose-200 underline"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={createLocalContact}
                className="text-sm font-semibold text-white underline"
              >
                Add a mock contact
              </button>
            </div>
            <p className="mt-2 text-xs text-rose-200">Use mock data to explore the drawer when the API is unavailable.</p>
          </div>
        )}
        <section className="rounded-3xl border border-white/10 bg-white/5 p-6">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading contacts…</div>
          ) : hasContacts ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {contacts.map((contact) => {
                const tags = Array.isArray(contact.tags) ? contact.tags : [];
                return (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => setSelectedContactId(contact.id)}
                    className="flex flex-col rounded-2xl border border-white/10 bg-slate-950/80 p-5 text-left transition hover:border-emerald-400/40"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {contact.name || "Unnamed contact"}
                        </h3>
                        <p className="text-xs uppercase tracking-[0.4em] text-slate-500">
                          {formatContactType(contact.type)}
                        </p>
                      </div>
                      <div className="text-xs rounded-full border border-white/20 bg-white/5 px-3 py-1 font-semibold uppercase tracking-[0.3em] text-slate-200">
                        {contact.contactListings?.length || 0} listings
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-slate-300">{renderPrimaryField(contact)}</p>
                    <div className="mt-4 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                      <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1">
                        Owner {contact.listingsOwned?.length || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1">
                        Updated {contact.updatedAt ? new Date(contact.updatedAt).toLocaleDateString("en-GB") : "—"}
                      </span>
                    </div>
                    {tags.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {tags.slice(0, 4).map((tag) => (
                          <span
                            key={tag}
                            className="text-[11px] rounded-full border border-white/10 bg-white/5 px-3 py-1 text-slate-200"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-slate-400">No contacts yet.</div>
          )}
        </section>
        <footer className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Page size</span>
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="rounded-2xl border border-white/10 bg-black/40 px-3 py-1 text-xs text-white"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handlePageChange(page - 1)}
              disabled={isFirstPage}
              className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white transition disabled:cursor-not-allowed disabled:text-slate-500"
            >
              Previous
            </button>
            <span>
              Page {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => handlePageChange(page + 1)}
              disabled={isLastPage}
              className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white transition disabled:cursor-not-allowed disabled:text-slate-500"
            >
              Next
            </button>
          </div>
        </footer>
      </div>
      <CrmContactDrawer
        open={Boolean(selectedContactId)}
        contactId={selectedContactId}
        onClose={() => setSelectedContactId(null)}
        onSaved={handleContactSaved}
        onDeleted={handleContactDeleted}
      />
    </>
  );
}

export default CrmContactsPage;
